import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="support-chat"
// AI/有人共通のサポートチャットウィジェット。数秒間隔のポーリングで更新する
export default class extends Controller {
  static targets = ["launcher", "panel", "messages", "input", "sendButton", "handoffButton", "status", "badge"]
  static values = {
    updatesUrl: String,
    messagesUrl: String,
    handoffUrl: String,
    aiLabel: String,
    operatorLabel: String,
    hasHistory: Boolean
  }

  static POLL_INTERVAL_MS = 4000
  // これ以上ビューポートが縮んでいたらソフトキーボードが出ていると見なす
  // (ブラウザのツールバーの伸縮と区別するための下限)
  static KEYBOARD_MIN_HEIGHT = 140
  // 閉じている間の未読チェック。閉じた直後はAIの返信が来やすいので短く、その後は間隔を空ける
  static CLOSED_POLL_INTERVAL_MS = 5000
  static CLOSED_SLOW_INTERVAL_MS = 30000
  static CLOSED_FAST_WINDOW_MS = 60000
  static SEEN_STORAGE_KEY = "tz_support_chat_last_seen_id"
  static TYPING_TIMEOUT_MS = 60000
  static MOBILE_BREAKPOINT = 576

  connect() {
    this.lastId = 0
    this.mode = "ai"
    this.lastSenderKind = null
    this.busy = false
    this.unread = 0
    this.lastSeenId = this.readLastSeenId()
    this.appliedHeight = null
    this.appliedOffset = null
    this.viewportRaf = null

    this.boundViewportSync = this.syncViewportHeight.bind(this)
    window.visualViewport?.addEventListener("resize", this.boundViewportSync)
    window.visualViewport?.addEventListener("scroll", this.boundViewportSync)
    window.addEventListener("orientationchange", this.boundViewportSync)

    this.boundOffsetSync = this.syncNavOffset.bind(this)
    window.addEventListener("resize", this.boundOffsetSync)
    window.addEventListener("orientationchange", this.boundOffsetSync)
    requestAnimationFrame(this.boundOffsetSync)
    // フォントの読み込みやログイン状態でナビの高さが変わるため、変化を監視する
    this.observeNav()

    // 閉じたままでも未読が分かるように、会話がある人だけ背面で確認する
    if (this.hasHistoryValue) this.startClosedPolling(this.constructor.CLOSED_SLOW_INTERVAL_MS)
  }

  disconnect() {
    this.stopPolling()
    this.stopClosedPolling()
    this.stopViewportTracking()
    this.unlockBody()
    window.visualViewport?.removeEventListener("resize", this.boundViewportSync)
    window.visualViewport?.removeEventListener("scroll", this.boundViewportSync)
    window.removeEventListener("orientationchange", this.boundViewportSync)
    window.removeEventListener("resize", this.boundOffsetSync)
    window.removeEventListener("orientationchange", this.boundOffsetSync)
    this.navObserver?.disconnect()
    this.navObserver = null
  }

  observeNav() {
    const nav = document.querySelector("footer.fixed-bottom")
    if (!nav || typeof ResizeObserver === "undefined") return

    this.navObserver = new ResizeObserver(this.boundOffsetSync)
    this.navObserver.observe(nav)
  }

  // turbo-frameのロード後、messagesターゲットが現れたら購読を始める
  messagesTargetConnected() {
    if (this.isOpen()) this.startPolling()
  }

  // 下部ナビに重なると押せないので、ナビが占めている高さ分だけ持ち上げる。
  // 高さはログイン状態やページで変わるため、実測して合わせる
  syncNavOffset() {
    const nav = document.querySelector("footer.fixed-bottom")
    let height = 0

    if (nav) {
      // position:fixed の要素は offsetParent が常に null になるため、
      // 表示の有無は矩形で判定する
      const rect = nav.getBoundingClientRect()
      if (rect.height > 0) {
        const fromBottom = window.innerHeight - rect.top
        // ツールバーの変動中に極端な値が出ても画面中央まで浮かないよう上限を設ける
        height = Math.round(Math.min(Math.max(rect.height, fromBottom), window.innerHeight * 0.35))
      }
    }

    this.element.style.setProperty("--sc-nav-height", `${height}px`)
  }

  // ---------------------------------------------------------------- open/close

  isOpen() {
    return this.hasPanelTarget && !this.panelTarget.hidden
  }

  isMobile() {
    return window.innerWidth < this.constructor.MOBILE_BREAKPOINT
  }

  toggle() {
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.panelTarget.hidden = false
    this.launcherTarget.setAttribute("aria-expanded", "true")
    this.clearUnread()
    this.stopClosedPolling()
    if (this.isMobile()) this.lockBody()
    this.scheduleViewportSync()
    if (this.hasMessagesTarget) {
      this.startPolling()
      this.scrollToBottom()
      // スマホでは開いた時点でキーボードを出さない。
      // 読むだけで閉じることが多い上に、開閉のたびにキーボードが動くと寸法が乱れる
      if (!this.isMobile()) this.inputTarget?.focus()
    }
  }

  close() {
    // 閉じる前に入力欄からフォーカスを外し、キーボードを確実に閉じる
    // (出したまま閉じると、次に開いたときに寸法が合わない)
    if (this.hasInputTarget) this.inputTarget.blur()
    this.stopViewportTracking()
    this.panelTarget.hidden = true
    this.resetPanelBox()
    this.launcherTarget.setAttribute("aria-expanded", "false")
    this.unlockBody()
    this.stopPolling()
    // 閉じた直後は返信が来やすいので短い間隔で確認し、しばらくしたら間隔を空ける
    this.startClosedPolling(this.constructor.CLOSED_POLL_INTERVAL_MS)
    this.slowDownTimer = setTimeout(() => {
      if (!this.isOpen()) this.startClosedPolling(this.constructor.CLOSED_SLOW_INTERVAL_MS)
    }, this.constructor.CLOSED_FAST_WINDOW_MS)
  }

  // ------------------------------------------------- 閉じている間の未読チェック

  startClosedPolling(interval) {
    this.stopClosedPolling({ keepSlowDown: true })
    this.checkForUnread()
    this.closedTimer = setInterval(() => this.checkForUnread(), interval)
  }

  stopClosedPolling({ keepSlowDown = false } = {}) {
    if (this.closedTimer) {
      clearInterval(this.closedTimer)
      this.closedTimer = null
    }
    if (!keepSlowDown && this.slowDownTimer) {
      clearTimeout(this.slowDownTimer)
      this.slowDownTimer = null
    }
  }

  // 閉じている間はパネルのDOMが無いこともあるため、描画はせず件数だけ数える。
  // lastIdを進めないので、開いたときに履歴がそのまま読み込まれる
  async checkForUnread() {
    if (this.isOpen() || document.hidden || this.busy) return

    this.busy = true
    try {
      // 既読位置以降だけを取りに行く(履歴を丸ごと運ばない)
      const response = await fetch(`${this.updatesUrlValue}?after=${this.lastSeenId}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) return

      const data = await response.json()
      // 会話が無い(保持期間切れなど)なら確認をやめる
      if (!data.mode) {
        this.stopClosedPolling()
        return
      }

      const unseen = data.messages.filter((message) => message.sender_kind !== "customer")
      this.setUnread(unseen.length)
    } catch {
      // 次回のポーリングで回復する
    } finally {
      this.busy = false
    }
  }

  // iOSはoverflow:hiddenだけでは背面スクロールが止まらないため、bodyを固定して
  // 元のスクロール位置をtopのマイナス値で保持する
  lockBody() {
    if (document.body.classList.contains("support-chat-open")) return

    this.savedScrollY = window.scrollY
    document.body.style.top = `${-this.savedScrollY}px`
    document.body.classList.add("support-chat-open")
  }

  unlockBody() {
    if (!document.body.classList.contains("support-chat-open")) return

    document.body.classList.remove("support-chat-open")
    document.body.style.top = ""
    window.scrollTo(0, this.savedScrollY || 0)
    this.savedScrollY = null
  }

  // モバイルのソフトキーボードでビューポートが縮んだ分だけ高さを合わせる。
  // iOSはキーボード表示時にビジュアルビューポート自体をずらすため、
  // offsetTop分だけパネルを移動させないと背面が覗いてしまう
  syncViewportHeight() {
    if (!this.hasPanelTarget) return

    if (!this.isOpen() || !this.isMobile()) return this.resetPanelBox()

    const viewport = window.visualViewport
    if (!viewport) return

    // ビューポートがずれている(offsetTop>0)なら、縮み方が小さくても必ず補正する。
    // 補正しないと、position:fixedのパネルが画面外の上へはみ出して見える
    const shrunk = window.innerHeight - viewport.height >= this.constructor.KEYBOARD_MIN_HEIGHT
    if (!shrunk && viewport.offsetTop <= 0) return this.resetPanelBox()

    this.applyPanelBox(Math.round(viewport.height), Math.round(viewport.offsetTop))
  }

  // 実際に変わったときだけ触る(毎フレーム呼ばれるため)
  applyPanelBox(height, offsetTop) {
    if (this.appliedHeight === height && this.appliedOffset === offsetTop) return

    this.appliedHeight = height
    this.appliedOffset = offsetTop
    this.panelTarget.style.height = `${height}px`
    this.panelTarget.style.transform = offsetTop ? `translateY(${offsetTop}px)` : ""
    this.scrollToBottom()
  }

  // インラインの指定を消してCSSの全画面表示に戻す
  resetPanelBox() {
    if (this.appliedHeight === null && this.appliedOffset === null) return

    this.appliedHeight = null
    this.appliedOffset = null
    this.panelTarget.style.height = ""
    this.panelTarget.style.transform = ""
  }

  // キーボードの開閉はアニメーションするため、落ち着いた頃にもう一度合わせる
  scheduleViewportSync() {
    ;[ 0, 120, 360 ].forEach((delay) => setTimeout(() => this.syncViewportHeight(), delay))
  }

  // キーボードが出ている間は毎フレーム合わせ続ける。
  // iOSはビューポートのずれを必ずしもイベントで知らせないため、
  // イベント頼みだと補正を取りこぼしてパネルがずれたままになる
  startViewportTracking() {
    if (this.viewportRaf) return

    const step = () => {
      this.syncViewportHeight()
      this.viewportRaf = requestAnimationFrame(step)
    }
    this.viewportRaf = requestAnimationFrame(step)
  }

  stopViewportTracking() {
    if (!this.viewportRaf) return

    cancelAnimationFrame(this.viewportRaf)
    this.viewportRaf = null
    this.scheduleViewportSync()
  }

  // ---------------------------------------------------------------- composing

  autoGrow() {
    const input = this.inputTarget
    input.style.height = "auto"
    input.style.height = `${Math.min(input.scrollHeight, 108)}px`
  }

  keydown(event) {
    if (event.key !== "Enter" || event.isComposing) return

    // スマホはEnterを改行として使う(送信は送信ボタン)。
    // PCはEnterで送信、Shift+Enterで改行
    if (this.isMobile() || event.shiftKey) return

    event.preventDefault()
    this.send(event)
  }

  async send(event) {
    event.preventDefault()
    const body = this.inputTarget.value.trim()
    if (body === "") return

    this.inputTarget.value = ""
    this.autoGrow()
    this.appendMessage({ sender_kind: "customer", body, occurred_at: new Date().toISOString() })

    // 楽観描画した自分の発言がポーリングと二重にならないようロックする
    this.busy = true
    try {
      const response = await this.request(this.messagesUrlValue, { body })
      if (!response) return

      if (response.message) this.lastId = Math.max(this.lastId, response.message.id)
      this.setMode(response.mode)
      this.setHandoffAvailable(response.handoff_available)
      if (this.mode === "ai") this.showTyping()
    } catch {
      // 送信失敗時は次のポーリングに任せる
    } finally {
      this.busy = false
    }
  }

  async callHuman() {
    this.handoffButtonTarget.disabled = true
    try {
      const response = await this.request(this.handoffUrlValue, {})
      // 受け付けられなかった場合(まだAIが応答していない等)はボタンを隠して次の更新に任せる
      if (!response) {
        this.setHandoffAvailable(false)
        return
      }

      this.setMode(response.mode)
      this.setHandoffAvailable(response.handoff_available)
      if (response.message) {
        this.lastId = Math.max(this.lastId, response.message.id)
        this.appendMessage(response.message)
      }
    } catch {
      // 失敗時はボタンを戻して再試行できるようにする
    } finally {
      this.handoffButtonTarget.disabled = false
    }
  }

  // ---------------------------------------------------------------- polling

  startPolling() {
    if (this.pollTimer) return

    this.fetchUpdates()
    this.pollTimer = setInterval(() => this.fetchUpdates(), this.constructor.POLL_INTERVAL_MS)
  }

  stopPolling() {
    if (!this.pollTimer) return

    clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  async fetchUpdates() {
    if (!this.hasMessagesTarget || this.busy) return

    this.busy = true
    try {
      const response = await fetch(`${this.updatesUrlValue}?after=${this.lastId}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) return

      const data = await response.json()
      if (data.mode) this.setMode(data.mode)
      this.setHandoffAvailable(data.handoff_available)

      data.messages.forEach((message) => {
        this.lastId = Math.max(this.lastId, message.id)
        if (message.sender_kind !== "customer") this.hideTyping()
        this.appendMessage(message)
        if (message.sender_kind !== "customer" && !this.isOpen()) this.addUnread()
      })
      // 開いている間に届いた分は読んだものとして扱う
      if (this.isOpen()) this.markSeen(this.lastId)
    } catch {
      // ネットワーク断は次回ポーリングで回復する
    } finally {
      this.busy = false
    }
  }

  // ---------------------------------------------------------------- rendering

  appendMessage({ sender_kind: senderKind, body, body_html: bodyHtml, occurred_at: occurredAt }) {
    const grouped = senderKind === this.lastSenderKind

    const row = document.createElement("div")
    row.className = `support-chat__row support-chat__row--${senderKind}`
    if (grouped) row.classList.add("support-chat__row--grouped")

    if (senderKind === "system") {
      row.appendChild(this.buildBubble(body, bodyHtml))
      this.messagesTarget.appendChild(row)
      this.lastSenderKind = senderKind
      this.scrollToBottom()
      return
    }

    if (senderKind !== "customer") {
      row.appendChild(this.buildAvatar(senderKind, grouped))
    }

    const stack = document.createElement("div")
    stack.className = "support-chat__stack"

    if (senderKind !== "customer" && !grouped) {
      const sender = document.createElement("div")
      sender.className = "support-chat__sender"
      sender.textContent = senderKind === "ai" ? this.aiLabelValue : this.operatorLabelValue
      stack.appendChild(sender)
    }

    stack.appendChild(this.buildBubble(body, bodyHtml))

    const time = document.createElement("div")
    time.className = "support-chat__time"
    time.textContent = this.formatTime(occurredAt)
    stack.appendChild(time)

    row.appendChild(stack)
    this.messagesTarget.appendChild(row)
    this.lastSenderKind = senderKind
    this.scrollToBottom()
  }

  // bodyHtmlはサーバー側でMarkdownを描画しサニタイズ済みのもの。
  // 無い場合(お客様自身の発言や旧データ)はそのまま文字として入れる
  buildBubble(body, bodyHtml) {
    const bubble = document.createElement("div")
    bubble.className = "support-chat__bubble"
    if (bodyHtml) {
      bubble.classList.add("support-chat__bubble--rich")
      bubble.innerHTML = bodyHtml
    } else {
      bubble.textContent = body
    }
    return bubble
  }

  buildAvatar(senderKind, grouped) {
    const avatar = document.createElement("div")
    avatar.className = `support-chat__row-avatar support-chat__row-avatar--${senderKind}`
    if (grouped) avatar.classList.add("support-chat__row-avatar--hidden")
    avatar.textContent = senderKind === "ai" ? "AI" : "S"
    return avatar
  }

  formatTime(value) {
    if (!value) return ""

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""

    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  showTyping() {
    this.hideTyping()

    const row = document.createElement("div")
    row.className = "support-chat__row support-chat__row--ai"
    row.dataset.supportChatTyping = "true"
    row.appendChild(this.buildAvatar("ai", false))

    const bubble = document.createElement("div")
    bubble.className = "support-chat__bubble support-chat__typing"
    bubble.append(document.createElement("span"), document.createElement("span"), document.createElement("span"))
    row.appendChild(bubble)

    this.messagesTarget.appendChild(row)
    this.scrollToBottom()

    this.typingTimeout = setTimeout(() => this.hideTyping(), this.constructor.TYPING_TIMEOUT_MS)
  }

  hideTyping() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
      this.typingTimeout = null
    }
    this.messagesTarget?.querySelectorAll("[data-support-chat-typing]").forEach((node) => node.remove())
  }

  setMode(mode) {
    if (!mode || mode === this.mode) return

    this.mode = mode
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.statusTarget.dataset[mode] || this.statusTarget.textContent
    }
  }

  // 「担当者と話す」の表示可否はサーバーの判定に従う。
  // AIが一度応答するまでは出さない(いきなり有人を選ばせない)
  setHandoffAvailable(available) {
    if (!this.hasHandoffButtonTarget) return

    this.handoffButtonTarget.hidden = !available
  }

  addUnread() {
    this.setUnread(this.unread + 1)
  }

  setUnread(count) {
    this.unread = count
    if (!this.hasBadgeTarget) return

    this.badgeTarget.textContent = count > 9 ? "9+" : count
    this.badgeTarget.hidden = count < 1
  }

  clearUnread() {
    this.setUnread(0)
    // 開いた時点で既読にする(未読はメッセージのIDで管理する)
    if (this.lastId > 0) this.markSeen(this.lastId)
  }

  // 既読位置。ページを読み込み直しても未読が復活しないよう端末に残す
  readLastSeenId() {
    try {
      return Number(window.localStorage.getItem(this.constructor.SEEN_STORAGE_KEY)) || 0
    } catch {
      return 0
    }
  }

  markSeen(id) {
    if (id <= this.lastSeenId) return

    this.lastSeenId = id
    try {
      window.localStorage.setItem(this.constructor.SEEN_STORAGE_KEY, String(id))
    } catch {
      // プライベートモードなどで書けない場合はこのページ内だけ保持する
    }
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return

    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  // ---------------------------------------------------------------- http

  async request(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(payload)
    })
    if (!response.ok) return null

    return response.json()
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
